import { upgradeTargetName } from "@highmarch/app";
import { BalanceData, PlayerProfile, QueuedUpgrade, UpgradeTarget, isQueued, nextUpgradeLevel, queueCapacity } from "@highmarch/domain";

/**
 * The upgrade queue (FINISH_PLAN D4, M3) as the hub shows it, with no DOM (queueModel.test.ts): what the upgrade
 * sheet's main button does for a target, and the queued jobs with when each should start. The rules are the domain's
 * (meta/upgradeQueue.ts); this only reads them.
 */

/**
 * The upgrade sheet's action for a target:
 * - `upgrade`: a builder is free and nothing runs or waits on it — start now;
 * - `queue`: every builder is busy, or the target is already upgrading or queued — queue the next level, paid now;
 * - `queueFull`: it would have to queue, and every builder's slot holds a job;
 * - `max`: nothing left to build.
 */
export type UpgradeAction =
  | { readonly kind: "upgrade" | "queue"; readonly toLevel: number; readonly cost: number; readonly affordable: boolean }
  | { readonly kind: "queueFull"; readonly toLevel: number }
  | { readonly kind: "max" };

export function upgradeAction(p: PlayerProfile, target: UpgradeTarget, towerIndex: number): UpgradeAction {
  const toLevel = nextUpgradeLevel(p, target, towerIndex);
  if (toLevel > p.balance.tables.maxLevel) return { kind: "max" };
  const cost = p.costFor(toLevel);
  const busy = p.isUpgrading(target, towerIndex) || isQueued(p, target, towerIndex);
  if (!busy && p.freeBuilders > 0) return { kind: "upgrade", toLevel, cost, affordable: p.canAfford(cost) };
  if (p.queue.length >= queueCapacity(p)) return { kind: "queueFull", toLevel };
  return { kind: "queue", toLevel, cost, affordable: p.canAfford(cost) };
}

/**
 * The upgrade can start now (a free builder, the gold in hand): what the green up-arrow on a tile means, and when the
 * sheet's UPGRADE pulses so the arrow leads straight to the button that clears it.
 */
export function startsNow(a: UpgradeAction): boolean {
  return a.kind === "upgrade" && a.affordable;
}

export interface QueueRow {
  readonly id: number;
  readonly target: UpgradeTarget;
  readonly towerIndex: number;
  /** "Fortress" or the tower's name. */
  readonly name: string;
  readonly toLevel: number;
  readonly cost: number;
  /** Seconds from now until it should start (0: as soon as the hub looks), from the running jobs' end times. */
  readonly startsIn: number;
}

/** The queue in order, with an estimate of when each starts (the domain starts them as builders and targets free up). */
export function queueRows(B: BalanceData, p: PlayerProfile, now: number): QueueRow[] {
  const key = (q: { target: UpgradeTarget; towerIndex: number }) => (q.target === UpgradeTarget.Base ? "base" : q.target === UpgradeTarget.Pet ? `p${q.towerIndex}` : `t${q.towerIndex}`);
  // When each builder is next free, and until when each target is busy: the same walk startQueued makes.
  const slots = [...Array.from({ length: Math.max(0, p.freeBuilders) }, () => now), ...p.jobs.map((j) => Math.max(now, j.endAt))].sort((a, b) => a - b);
  const busyUntil = new Map<string, number>(p.jobs.map((j) => [key(j), Math.max(now, j.endAt)]));
  return p.queue.map((q: QueuedUpgrade) => {
    const slot = slots.shift() ?? now;
    const start = Math.max(slot, busyUntil.get(key(q)) ?? now);
    const end = start + p.durationFor(q.toLevel);
    slots.push(end);
    slots.sort((a, b) => a - b);
    busyUntil.set(key(q), end);
    return { id: q.id, target: q.target, towerIndex: q.towerIndex, name: upgradeTargetName(B, q.target, q.towerIndex), toLevel: q.toLevel, cost: q.cost, startsIn: Math.max(0, start - now) };
  });
}

/** The queue's fill for the Towers tab: slots used and slots there are (one per builder). */
export function queueFill(p: PlayerProfile): { used: number; capacity: number } {
  return { used: p.queue.length, capacity: queueCapacity(p) };
}
